import {v4 as uuid} from 'uuid';
import FloorplanWidget from './floorplan/FloorplanWidget';
import FloorplanWidgetConfig from './floorplan/config/FloorplanWidgetConfig';
import InfoWidget from './info/InfoWidget';
import InfoWidgetConfig from './info/config/InfoWidgetConfig';
import {DEFAULT_ROWS} from './info/config/ConfigUtils';
import DigitalTwinWidget from './digitaltwin/DigitalTwinWidget';
import DigitalTwinWidgetConfig from './digitaltwin/config/DigitalTwinWidgetConfig';
import {DashboardWidgetType, WidgetDefinition} from '@deviceinsight/ng-ui-scale-lib/api';
import KpiSingleValueWidget from './kpi/singlevalue/KpiSingleValueWidget';
import KpiSingleValueWidgetConfig from './kpi/singlevalue/config/KpiSingleValueWidgetConfig';
import {
	KpiGroupBy,
	KpiQuery,
	PERIOD_COUNT_DEFAULT,
	PERIOD_TIMEFRAME_DEFAULT,
	PERIOD_TYPE_DEFAULT
} from './kpi/common/kpiUtils';
import KpiAggregatedChartWidget from './kpi/aggregatedchart/KpiAggregatedChartWidget';
import KpiAggregatedChartWidgetConfig from './kpi/aggregatedchart/config/KpiAggregatedChartWidgetConfig';
import KpiSingleValueColumnWidget from './kpi/singlevaluecolumn/KpiSingleValueColumnWidget';
import KpiSingleValueColumnWidgetConfig from './kpi/singlevaluecolumn/config/KpiSingleValueColumnWidgetConfig';
import KpiPieChartWidget from './kpi/piechart/KpiPieChartWidget';
import KpiPieChartWidgetConfig from './kpi/piechart/config/KpiPieChartWidgetConfig';
import KpiSinglePeriodChartWidget from './kpi/singleperiodchart/KpiSinglePeriodChartWidget';
import KpiSinglePeriodChartWidgetConfig from './kpi/singleperiodchart/config/KpiSinglePeriodChartWidgetConfig';

export type RafiWidgetDefinition = Omit<WidgetDefinition, 'name'> & {
	name: DashboardWidgetType | 'rafiFloorplanWidget' | 'info' | 'digitalTwin';
};

export const floorplanWidget: RafiWidgetDefinition = {
	name: 'rafiFloorplanWidget',
	timeDriveSupport: true,
	widgetComponent: FloorplanWidget,
	configComponent: FloorplanWidgetConfig,
	autoOpenConfig: true,
	title: {
		id: 'dashboard.edit.rafiFloorplanWidget.name',
		defaultValue: 'Floorplan '
	},
	subtitle: {
		id: 'dashboard.edit.rafiFloorplanWidget.subTitle',
		defaultValue: 'A group floorplan'
	},
	scope: ['assetGroup']
};

export const info: RafiWidgetDefinition = {
	name: 'info',
	widgetComponent: InfoWidget,
	configComponent: InfoWidgetConfig,
	autoOpenConfig: true,
	title: {
		id: 'dashboard.edit.info.name',
		defaultValue: 'Info'
	},
	defaultSettings: {
		items: [],
		rows: DEFAULT_ROWS
	},
	scope: ['asset', 'gateway']
};

export const digitalTwin: RafiWidgetDefinition = {
	name: 'digitalTwin',
	timeDriveSupport: true,
	widgetComponent: DigitalTwinWidget,
	configComponent: DigitalTwinWidgetConfig,
	autoOpenConfig: false,
	title: {
		id: 'dashboard.edit.digitaltwin.name',
		defaultValue: 'Digital Twin'
	},
	defaultSettings: {
		key: uuid()
	},
	scope: ['asset', 'gateway']
};

export const kpiWidgets: WidgetDefinition[] = [
	{
		name: 'kpiSingleValue',
		widgetComponent: KpiSingleValueWidget,
		configComponent: KpiSingleValueWidgetConfig,
		autoOpenConfig: true,
		disabledByDefault: false,
		title: {
			id: 'dashboard.edit.kpisinglevalue.name',
			defaultValue: 'Kpi '
		},
		subtitle: {
			id: 'dashboard.edit.kpisinglevalue.subTitle',
			defaultValue: 'Single Value'
		},
		scope: ['asset', 'gateway', 'assetGroup'],
		defaultSettings: {
			kpiName: '',
			aggregationType: KpiQuery.SUM,
			periodTimeframe: PERIOD_TIMEFRAME_DEFAULT,
			periodType: PERIOD_TYPE_DEFAULT,
			periodCount: PERIOD_COUNT_DEFAULT,
			workingShiftEnabled: false,
			workingShiftDays: []
		},
		timeDriveSupport: false
	},
	{
		name: 'kpiAggregatedChart',
		widgetComponent: KpiAggregatedChartWidget,
		configComponent: KpiAggregatedChartWidgetConfig,
		autoOpenConfig: true,
		disabledByDefault: false,
		title: {
			id: 'dashboard.edit.kpiaggregatetchart.name',
			defaultValue: 'Kpi '
		},
		subtitle: {
			id: 'dashboard.edit.kpiaggregatetchart.subTitle',
			defaultValue: 'Aggregated Chart'
		},
		scope: ['asset', 'gateway', 'assetGroup'],
		defaultSettings: {
			items: [],
			aggregationType: KpiQuery.SUM,
			groupBy: KpiGroupBy.HOUR,
			periodTimeframe: PERIOD_TIMEFRAME_DEFAULT,
			periodType: PERIOD_TYPE_DEFAULT,
			periodCount: PERIOD_COUNT_DEFAULT,
			workingShiftEnabled: false
		},
		timeDriveSupport: false
	},
	{
		name: 'kpiSingleValueColumn',
		timeDriveSupport: true,
		widgetComponent: KpiSingleValueColumnWidget,
		configComponent: KpiSingleValueColumnWidgetConfig,
		autoOpenConfig: true,
		title: {
			id: 'dashboard.edit.kpisinglevaluecolumn.name',
			defaultValue: 'Kpi '
		},
		subtitle: {
			id: 'dashboard.edit.kpisinglevaluecolumn.subTitle',
			defaultValue: 'Single Value Column'
		},
		scope: ['asset', 'gateway', 'assetGroup'],
		disabledByDefault: false,
		defaultSettings: {
			kpiName: '',
			unit: '',
			aggregationType: KpiQuery.SUM,
			periodTimeframe: PERIOD_TIMEFRAME_DEFAULT,
			periodType: PERIOD_TYPE_DEFAULT,
			periodCount: PERIOD_COUNT_DEFAULT,
			workingShiftEnabled: false,
			workingShiftDays: [],
			zones: [
				{
					color: '#0077BB',
					value: '100000000'
				}
			]
		}
	},
	{
		name: 'kpiPieChart',
		timeDriveSupport: true,
		widgetComponent: KpiPieChartWidget,
		configComponent: KpiPieChartWidgetConfig,
		autoOpenConfig: true,
		disabledByDefault: false,
		title: {
			id: 'dashboard.edit.piekpichart.name',
			defaultValue: 'Kpi'
		},
		subtitle: {
			id: 'dashboard.edit.piekpichart.subTitle',
			defaultValue: 'Pie Chart'
		},
		scope: ['asset', 'gateway', 'assetGroup'],
		defaultSettings: {
			items: [],
			aggregationType: KpiQuery.SUM,
			periodTimeframe: PERIOD_TIMEFRAME_DEFAULT,
			periodType: PERIOD_TYPE_DEFAULT,
			periodCount: PERIOD_COUNT_DEFAULT,
			workingShiftEnabled: false
		}
	},
	{
		name: 'kpiSinglePeriodChart',
		timeDriveSupport: true,
		widgetComponent: KpiSinglePeriodChartWidget,
		configComponent: KpiSinglePeriodChartWidgetConfig,
		autoOpenConfig: true,
		disabledByDefault: false,
		title: {
			id: 'dashboard.edit.singleperiodkpi.name',
			defaultValue: 'Kpi'
		},
		subtitle: {
			id: 'dashboard.edit.singleperiodkpi.subTitle',
			defaultValue: 'Single Period Chart'
		},
		scope: ['asset', 'gateway', 'assetGroup'],
		defaultSettings: {
			items: [],
			aggregationType: KpiQuery.SUM,
			periodTimeframe: PERIOD_TIMEFRAME_DEFAULT,
			periodType: PERIOD_TYPE_DEFAULT,
			periodCount: PERIOD_COUNT_DEFAULT,
			workingShiftEnabled: false
		}
	}
];
